import React from 'react';

import { AudioListenButton } from '@atlassian/ai-audio';

import { useAudioContentQuery } from '@confluence/audio/entry-points/useAudioContentQuery.tsx';
import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import type { EditorPropertyType } from '@confluence/content-utils/entry-points/EditorPropertyType.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { AudioButtonSpotlight as PostOfficeAudioButtonSpotlight } from '@confluence/experiment-audio-button-changeboarding';

type AudioListenButtonWrapperProps = {
	isRovoButtonEnabled: boolean;
	contentId: string;
	contentType: string;
	spaceKey: string;
	title: string;
	isAutoOpenDisabled?: boolean;
};

type UseActiveContentProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	data: ReturnType<typeof useAudioContentQuery>['data'];
	title: string;
};

const useActiveContent = ({
	contentId,
	contentType,
	spaceKey,
	data,
	title: currentTitle,
}: UseActiveContentProps) => {
	const title = currentTitle || (data?.singleContent?.title as string);
	const ownerName = data?.singleContent?.history?.ownedBy?.displayName;
	const authorName = data?.singleContent?.history?.createdBy?.displayName;
	const ownerAvatarUrl = data?.singleContent?.history?.ownedBy?.profilePicture?.path;
	const authorAvatarUrl = data?.singleContent?.history?.createdBy?.profilePicture?.path;
	const baseLink = data?.singleContent?.links?.base || '';
	const webuiLink = data?.singleContent?.links?.webui || '';

	const shouldUsePageOwner = ownerName && contentType === 'page';
	const contentAvatarName = shouldUsePageOwner ? ownerName : authorName;
	const contentAvatarUrl = shouldUsePageOwner ? ownerAvatarUrl : authorAvatarUrl;
	const editorVersion = data?.singleContent?.properties?.nodes
		?.find((prop) => prop?.key === 'editor')
		?.value?.replace(/['"]+/g, '') as EditorPropertyType;
	const mainContentLink = React.useMemo(() => {
		return editorVersion === 'v2'
			? baseLink +
					getURLBypassingResumeDraftAction({
						url: webuiLink,
						spaceKey,
						contentType,
						editorVersion,
						contentId,
						noContextPath: true,
					})
			: baseLink + webuiLink;
	}, [editorVersion, spaceKey, contentType, contentId, baseLink, webuiLink]);
	const emojiId = data?.singleContent?.properties?.nodes?.find(
		(prop) => prop?.key === 'emoji-title-published',
	)?.value;
	const subType = data?.singleContent?.subType;

	return React.useMemo(
		() => ({
			contentId,
			contentType,
			subType,
			spaceKey,
			title,
			author: contentAvatarName,
			avatarUrl: contentAvatarUrl,
			supportingContent: [],
			mainContentLink,
			emojiId,
		}),
		[
			contentId,
			contentType,
			subType,
			spaceKey,
			title,
			contentAvatarName,
			contentAvatarUrl,
			emojiId,
			mainContentLink,
		],
	);
};

export const AudioListenButtonWrapper = ({
	isRovoButtonEnabled,
	isAutoOpenDisabled,
	contentId,
	...props
}: AudioListenButtonWrapperProps) => {
	const { data } = useAudioContentQuery({ contentId });
	const activeContent = useActiveContent({ contentId, data, ...props });

	if (expValEquals('confluence_audio_v3_changeboarding', 'isEnabled', true)) {
		return (
			<>
				<PostOfficeAudioButtonSpotlight />
				<AudioListenButton
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					activeContent={activeContent}
					isAutoOpenDisabled={!data?.singleContent || !!isAutoOpenDisabled}
					source="contentWrapper"
				/>
			</>
		);
	}

	return (
		<AudioListenButton
			shape={isRovoButtonEnabled ? 'default' : 'circle'}
			activeContent={activeContent}
			isAutoOpenDisabled={!data?.singleContent || !!isAutoOpenDisabled}
			source="contentWrapper"
		/>
	);
};
